import { useFormik } from "formik";
import { Button, Col, Form, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import InputText from "../../../Forms/Element/InputText";
import SaveIcon from "../../../Icon/SaveIcon";
import CancelIcon from "../../../Icon/CancelIcon";
import ZipCodeInput from "../../../Common/ZipCodeInput";
import AsteriskRequired from "../../../Common/AsteriskRequired";
import { errorAlert } from "../../../alert/defaultAlert";
import axiosInstance from "../../../../../services/AxiosInstance";
import { useEffect, useRef, useState } from "react";
import LoadingIcon from "../../../Icon/LoadingIcon";
import Api from "../../../../../constants/Api";
import HomeIcon from "../../../Icon/HomeIcon";
import WorkIcon from "../../../Icon/WorkIcon";
import FavoriteType from "./FavoriteType";
import { searchAddressByZipCode } from "./AddressUtils";

/**
 * Cadastro e edição de um endereço do cliente, aberto em modal.
 *
 * @param {string} submitLabel rótulo do botão de salvar. Existe para o fluxo que segue
 *                             adiante depois de salvar (ex.: "Salvar e gerar link"), em que
 *                             só "Salvar" esconderia que o próximo passo é automático.
 */
const AddressNew = (props) => {

    const { t } = useTranslation();
    const { setShow, parentId, gridRef, entityId, setEntityId, onSaved, submitLabel } = props;
    const [showLoading, setShowLoading] = useState(false);
    // Último CEP consultado, só com dígitos. Evita repetir a consulta quando o campo
    // dispara de novo com o mesmo valor, e na edição impede que o CEP já salvo
    // sobrescreva o logradouro que o operador ajustou à mão.
    const [searchedZipCode, setSearchedZipCode] = useState(null);
    const formRef = useRef(null);
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [favoriteType, setFavoriteType] = useState(FavoriteType.HOME);

    // Define o schema de validação do form.
    const formSchema = Yup.object().shape({
        street: Yup.string()
            .min(3, t("validation.min.generic", { field: t('address.street'), qty: 3 }))
            .required(t('validation.required.address.street')),
        number: Yup.string().required(t('validation.required.address.number')),
        complement: Yup.string().optional(),
        neighborhood: Yup.string().required(t('validation.required.address.neighborhood')),
        city: Yup.string().required(t('validation.required.address.city')),
        state: Yup.string().required(t('validation.required.address.state')),
        zipCode: Yup.string().required(t('validation.required.address.zipCode')),
        referencePoint: Yup.string().optional(),
    });

    // Define o formik.
    const formik = useFormik({
        initialValues: {},
        validationSchema: formSchema,
        onSubmit: values => {
            if (parentId === null) {
                errorAlert("validation.required.parentId");
                return;
            }

            let entity = {
                id: entityId,
                address: {
                    street: values.street,
                    number: values.number,
                    complement: values.complement ? values.complement : '',
                    neighborhood: values.neighborhood,
                    city: values.city,
                    state: values.state,
                    zipCode: values.zipCode,
                    referencePoint: values.referencePoint ? values.referencePoint : '',
                    favoriteType: favoriteType,
                    latitude: latitude,
                    longitude: longitude,
                },
                parentId: parentId
            }

            // Devolver a promessa faz o formik manter isSubmitting até a resposta, e é
            // isso que trava o botão: dois cliques rápidos criavam o endereço duplicado.
            return axiosInstance.post(Api.main.customer_address, entity)
                .then((resp) => {
                    const savedAddress = resp.data;
                    const gridApi = gridRef?.current?.api;

                    if (!gridApi) {
                        // Aberto fora da listagem (ex.: cadastro de assinante): não há grade
                        // para atualizar, e quem abriu segue o fluxo pelo onSaved.
                        setShow(false);
                    } else if (gridApi.refreshInfiniteCache) {
                        // Este grid usa o modelo infinite; transações locais não alteram
                        // o cache dele. Recarregar o cache mantém a paginação consistente.
                        gridApi.refreshInfiniteCache();
                        setShow(false);
                    } else if (gridApi && savedAddress?.id != null) {
                        // O POST já devolve o registro persistido; inseri-lo diretamente
                        // evita que o grid fique vazio enquanto a busca de filtros termina.
                        gridApi.applyTransaction({
                            [entityId == null ? 'add' : 'update']: [savedAddress]
                        });
                        setShow(false);
                    } else {
                        // Fallback para respostas antigas que não retornam o endereço salvo.
                        axiosInstance.post(Api.main.customer_address + "/findByFilters", {
                            filters: [{ field: "parentId", value: parentId, operation: "EQUAL" }]
                        })
                            .then((filterResponse) => {
                                gridApi?.setGridOption('rowData', filterResponse.data);
                                setShow(false);
                            })
                            .catch(error => errorAlert(error));
                    }

                    onSaved?.(savedAddress);
                    setEntityId(null);
                })
                .catch(error => errorAlert(error));
        },
    });

    /**
     * Preenche logradouro, bairro, cidade e UF a partir do CEP, com a mesma consulta das
     * demais telas de endereço.
     *
     * Dispara assim que o CEP fica completo, sem esperar o operador sair do campo. CEP
     * incompleto ou igual ao último consultado não gera chamada.
     */
    const searchZipCode = (value) => {
        const digits = (value || '').replace(/\D/g, '');
        if (digits.length !== 8 || digits === searchedZipCode) {
            return;
        }

        setSearchedZipCode(digits);
        setShowLoading(true);
        searchAddressByZipCode(digits)
            .then((data) => {
                formik.setFieldValue('street', data.street || '');
                formik.setFieldValue('neighborhood', data.neighborhood || '');
                formik.setFieldValue('city', data.city || '');
                formik.setFieldValue('state', data.state || '');
                setLatitude(data.latitude);
                setLongitude(data.longitude);

                // O CEP não traz o número, que passa a ser o único campo obrigatório
                // pendente. Levar o cursor até ele poupa um clique no celular.
                formRef.current?.querySelector('input[name="number"]')?.focus();
            })
            .catch((error) => {
                // Libera uma nova tentativa com o mesmo CEP: a falha pode ter sido de rede.
                setSearchedZipCode(null);
                errorAlert(t(error.message));
            })
            .finally(() => {
                setShowLoading(false);
            });
    }

    useEffect(() => {
        if (entityId !== null) {
            axiosInstance.get(Api.main.customer_address + "/" + entityId)
                .then((resp) => {
                    let data = resp.data;
                    formik.setFieldValue('street', data.address.street);
                    formik.setFieldValue('neighborhood', data.address.neighborhood);
                    formik.setFieldValue('city', data.address.city);
                    formik.setFieldValue('state', data.address.state);
                    formik.setFieldValue('zipCode', data.address.zipCode);
                    formik.setFieldValue('referencePoint', data.address.referencePoint);
                    formik.setFieldValue('number', data.address.number);
                    formik.setFieldValue('complement', data.address.complement);

                    setFavoriteType(FavoriteType.HOME);
                    if (data.address.favoriteType === "WORK") {
                        setFavoriteType(FavoriteType.WORK);
                    }

                    setLatitude(data.address.latitude);
                    setLongitude(data.address.longitude);
                    setSearchedZipCode((data.address.zipCode || '').replace(/\D/g, ''));
                })
                .catch(error => errorAlert(error));
        }

        // eslint-disable-next-line
    }, [entityId]);

    return (
        <>
            <Form ref={formRef}>
                <Row className="mb-3">
                    <Col className="col-5">
                        <FormGroup>
                            <FormLabel>{t('address.zipCode')} <AsteriskRequired /></FormLabel>
                            <ZipCodeInput
                                name="zipCode"
                                inputMode="numeric"
                                autoComplete="postal-code"
                                onChange={(event) => {
                                    formik.handleChange(event);
                                    searchZipCode(event.target.value);
                                }}
                                onBlur={formik.handleBlur}
                                value={formik.values.zipCode}
                                error={formik.errors.zipCode}
                            />
                        </FormGroup>
                    </Col>
                    {/* Ao lado do CEP, e não abaixo, para o aviso não empurrar os campos
                        enquanto a consulta roda. Sem consulta, ensina que ela existe. */}
                    <Col className="col-7 d-flex align-items-end">
                        <small className="text-muted pb-2">
                            {showLoading
                                ? <><LoadingIcon /> {t('address_searching')}</>
                                : t('address_zip_autofill_hint')}
                        </small>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col className="col-8">
                        <FormGroup>
                            <FormLabel>{t('address.street')} <AsteriskRequired /></FormLabel>
                            <InputText
                                name="street"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.street}
                                error={formik.errors.street}
                                maxLength="50"
                            />
                        </FormGroup>
                    </Col>
                    <Col className="col-4">
                        <FormGroup>
                            <FormLabel>{t('address.number')} <AsteriskRequired /></FormLabel>
                            <InputText
                                name="number"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.number}
                                error={formik.errors.number}
                                maxLength="10"
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col className="col-6">
                        <FormGroup>
                            <FormLabel>{t('address.complement')}</FormLabel>
                            <InputText
                                name="complement"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.complement}
                                error={formik.errors.complement}
                                maxLength="30"
                            />
                        </FormGroup>
                    </Col>
                    <Col className="col-6">
                        <FormLabel>{t('address.neighborhood')} <AsteriskRequired /></FormLabel>
                        <InputText
                            name="neighborhood"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.neighborhood}
                            error={formik.errors.neighborhood}
                            maxLength="30"
                        />
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col className="col-8">
                        <FormLabel>{t('address.city')} <AsteriskRequired /></FormLabel>
                        <InputText
                            name="city"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.city}
                            error={formik.errors.city}
                            maxLength="30"
                        />
                    </Col>
                    <Col className="col-4">
                        <FormLabel>{t('address.state')} <AsteriskRequired /></FormLabel>
                        <InputText
                            name="state"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.state}
                            error={formik.errors.state}
                            maxLength="8"
                        />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col className="col-12">
                        <FormLabel>{t('address.referencePoint')}</FormLabel>
                        <InputText
                            name="referencePoint"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.referencePoint}
                            error={formik.errors.referencePoint}
                            maxLength="50"
                        />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col className="col-12">
                        <div className="form-check form-check-inline">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="favoriteType"
                                id="homeFavoriteType"
                                defaultChecked={favoriteType === FavoriteType.HOME ? favoriteType : null}
                                checked={favoriteType === FavoriteType.HOME ? favoriteType : null}
                                onClick={() => { setFavoriteType(FavoriteType.HOME) }}
                            />
                            <label className="form-check-label" for="homeFavoriteType"><HomeIcon /> {t('home')}</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="favoriteType"
                                id="workFavoriteType"
                                checked={favoriteType === FavoriteType.WORK ? favoriteType : null}
                                onClick={() => { setFavoriteType(FavoriteType.WORK) }}
                            />
                            <label className="form-check-label" for="workFavoriteType"><WorkIcon /> {t('work')}</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="favoriteType"
                                id="otherFavoriteType"
                                checked={favoriteType === FavoriteType.OTHER}
                                onChange={() => setFavoriteType(FavoriteType.OTHER)}
                            />
                            <label className="form-check-label" for="otherFavoriteType">Outro</label>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col className="col-12">
                        <Button
                            variant="primary"
                            className="me-2"
                            disabled={formik.isSubmitting || showLoading}
                            onClick={() => formik.handleSubmit()}>
                            {formik.isSubmitting ? <LoadingIcon /> : <SaveIcon />} {submitLabel || t('save')}
                        </Button>
                        <Button
                            variant="danger"
                            className="me-2"
                            disabled={formik.isSubmitting}
                            onClick={() => setShow(false)}>
                            <CancelIcon /> {t('cancel')}
                        </Button>
                    </Col>
                </Row>
            </Form>
        </>
    );
}

export default AddressNew;
